/**
 * PCK-213 - one attachment size calculation for every sender.
 *
 * VueEmail and PrintPreview each had their own pair of totalAttachmentSize/combinedAttachments
 * computeds over the same emailer, with rules that had drifted: one deduped by filename and the
 * other did not, one counted the report from reportBytes unconditionally while the other matched
 * it by name, and the customAttachments chains differed. They could disagree about the same email.
 *
 * sizeOf returns null when a size cannot be established, rather than 0. Counting an unknown as
 * zero is what let an oversized send show a green meter.
 */

const nameOf = (item) => (item && (item.fileName || item.filename || item.name)) || null

const sizeOf = (item) => {
  if (!item) return null
  if (item.fileBytes && typeof item.fileBytes.byteLength === 'number') return item.fileBytes.byteLength
  if (typeof item.originalFileSize === 'number') return item.originalFileSize
  if (typeof item.fileSize === 'number') return item.fileSize
  return null
}

export function buildAttachmentList(emailer, { reportName = null, reportBytes = null } = {}) {
  const list = []
  const seen = []
  const add = (fileName, fileSize) => {
    if (!fileName || seen.includes(fileName)) return
    seen.push(fileName)
    list.push({ fileName, fileSize })
  }

  if (!emailer) return list

  const reportSize = reportBytes ? reportBytes.byteLength || reportBytes.length || null : null

  // `attachments` holds bare filename strings for senders that attach a generated report.
  // send()/sendExport() identify it with attachments.indexOf(reportName), so the same match
  // gives us its size. Other strings carry no size and stay null.
  if (Array.isArray(emailer.attachments)) {
    for (const att of emailer.attachments) {
      if (typeof att === 'string') add(att, att === reportName ? reportSize : null)
      else add(nameOf(att), sizeOf(att))
    }
  }

  for (const key of ['customAttachments', 'quoteAttachments', 'quoteImageAttachments']) {
    if (Array.isArray(emailer[key])) {
      for (const item of emailer[key]) add(nameOf(item), sizeOf(item))
    }
  }

  return list
}

export function sumAttachmentSize(list) {
  return (list || []).reduce((total, item) => total + (item.fileSize || 0), 0)
}

export default { buildAttachmentList, sumAttachmentSize }
